import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "className", "closeOnClick", "delay", "disabled", "handle", "payload", "render", "style", "xstyle"],
  _excluded2 = ["align", "anchor", "children", "className", "keepMounted", "side", "style", "xstyle"];
import { c as _c } from "react-compiler-runtime";
import { forwardRef } from 'react';
import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip';
import * as stylex from '@stylexjs/stylex';
import { theme } from 'trellis-theming/modern-theme.stylex';
import { focusRingOutset } from '../../styles/focus-ring';
import { LayoutConstraintsReset } from '../layout/LayoutConstraints';
import { zIndexVars } from '../../constants/zIndex.stylex';
import { assertElementRender } from '../../utils/assertElementRender';
import { warnMutuallyExclusive } from '../../utils/warnMutuallyExclusive';
import { DATA_COMPONENT_ATTRIBUTE } from '../../constants/dataAttributes';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TOOLTIP_COMPONENT_NAME = 'Tooltip';
const TOOLTIP_TRIGGER_COMPONENT_NAME = 'TooltipTrigger';
const TOOLTIP_CONTENT_COMPONENT_NAME = 'TooltipContent';
export const createTooltipHandle = BaseTooltip.createHandle;
const styles = {
  trigger: {
    alignItems: "x6s0dn4",
    backgroundColor: "x1gjadoe",
    borderStyle: "xng3xce",
    display: "x3nfvp2",
    justifyContent: "xl56j7k",
    $$css: true
  },
  popup: {
    backgroundColor: "x122buio",
    borderRadius: "x1nq0khy",
    color: "x69kfgm",
    fontFamily: "x6hfyku",
    fontSize: "xe639uq",
    fontWeight: "x1h7urat",
    letterSpacing: "x1ibk7ho",
    lineHeight: "x1x6p1lu",
    maxWidth: "x16em4ew",
    opacity: "x1hc1fzr x1iwdfne",
    overflowWrap: "x1mzt3pk",
    paddingBlock: "x1n5c368",
    paddingInline: "x17dj6ds",
    transform: "x3oybdh x49noix",
    transformOrigin: "xctpfpq",
    transitionDuration: "xx6bhzk x1wxhr3b x12w9bfk",
    transitionProperty: "x6o7n8i",
    transitionTimingFunction: "xwji4o3",
    $$css: true
  }
};

/**
 * Displays a short contextual label or hint when the user hovers or focuses an element. Use for icon-only button labels and supplementary field hints; for interactive content, use Popover instead.
 */
export function Tooltip(t0) {
  const $ = _c(8);
  const {
    children,
    defaultOpen,
    disabled,
    disableHoverablePopup,
    handle,
    onOpenChange,
    open
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== defaultOpen || $[2] !== disableHoverablePopup || $[3] !== disabled || $[4] !== handle || $[5] !== onOpenChange || $[6] !== open) {
    t1 = /*#__PURE__*/_jsx(BaseTooltip.Root, {
      defaultOpen: defaultOpen,
      disabled: disabled,
      disableHoverablePopup: disableHoverablePopup,
      handle: handle,
      onOpenChange: onOpenChange,
      open: open,
      children: children
    });
    $[0] = children;
    $[1] = defaultOpen;
    $[2] = disableHoverablePopup;
    $[3] = disabled;
    $[4] = handle;
    $[5] = onOpenChange;
    $[6] = open;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
Tooltip.displayName = TOOLTIP_COMPONENT_NAME;

/**
 * Element that opens the tooltip on hover or focus. Pass non-interactive content (icons, text) as `children` — the trigger wraps them in a focusable `<button>`. Use the `render` prop to replace the default button with an existing interactive element like `Button`. For detached triggers, pass a shared `handle` created with `createTooltipHandle()`.
 */
export const TooltipTrigger = /*#__PURE__*/forwardRef((t0, ref) => {
  const $ = _c(25);
  let children;
  let closeOnClick;
  let delay;
  let disabled;
  let handle;
  let payload;
  let render;
  let restProps;
  let xstyle;
  if ($[0] !== t0) {
    const {
        children: t1,
        closeOnClick: t2,
        delay: t3,
        disabled: t4,
        handle: t5,
        payload: t6,
        render: t7,
        xstyle: t8
      } = t0,
      t9 = _objectWithoutPropertiesLoose(t0, _excluded);
    children = t1;
    closeOnClick = t2;
    delay = t3;
    disabled = t4;
    handle = t5;
    payload = t6;
    render = t7;
    xstyle = t8;
    restProps = t9;
    $[0] = t0;
    $[1] = children;
    $[2] = closeOnClick;
    $[3] = delay;
    $[4] = disabled;
    $[5] = handle;
    $[6] = payload;
    $[7] = render;
    $[8] = restProps;
    $[9] = xstyle;
  } else {
    children = $[1];
    closeOnClick = $[2];
    delay = $[3];
    disabled = $[4];
    handle = $[5];
    payload = $[6];
    render = $[7];
    restProps = $[8];
    xstyle = $[9];
  }
  assertElementRender("TooltipTrigger", render);
  warnMutuallyExclusive("TooltipTrigger", render, xstyle);
  let t1;
  if ($[10] !== render || $[11] !== xstyle) {
    t1 = stylex.props(render == null && styles.trigger, render == null && focusRingOutset.default, render == null ? xstyle : undefined);
    $[10] = render;
    $[11] = xstyle;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  let t2;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      [DATA_COMPONENT_ATTRIBUTE]: TOOLTIP_TRIGGER_COMPONENT_NAME
    };
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  let t3;
  if ($[14] !== children || $[15] !== closeOnClick || $[16] !== delay || $[17] !== disabled || $[18] !== handle || $[19] !== payload || $[20] !== ref || $[21] !== render || $[22] !== restProps || $[23] !== t1) {
    t3 = /*#__PURE__*/_jsx(BaseTooltip.Trigger, Object.assign({}, restProps, {
      closeOnClick: closeOnClick,
      delay: delay,
      disabled: disabled,
      handle: handle,
      payload: payload,
      ref: ref,
      render: render
    }, t1, t2, {
      children: children
    }));
    $[14] = children;
    $[15] = closeOnClick;
    $[16] = delay;
    $[17] = disabled;
    $[18] = handle;
    $[19] = payload;
    $[20] = ref;
    $[21] = render;
    $[22] = restProps;
    $[23] = t1;
    $[24] = t3;
  } else {
    t3 = $[24];
  }
  return t3;
});
TooltipTrigger.displayName = TOOLTIP_TRIGGER_COMPONENT_NAME;

/**
 * Portaled popup that displays the tooltip label. Renders a caret arrow pointing toward the trigger and positions itself on the specified `side`.
 */
export const TooltipContent = /*#__PURE__*/forwardRef((t0, ref) => {
  const $ = _c(29);
  let anchor;
  let children;
  let restProps;
  let t1;
  let t2;
  let t3;
  let xstyle;
  if ($[0] !== t0) {
    const {
        align: t4,
        anchor: t5,
        children: t6,
        keepMounted: t7,
        side: t8,
        xstyle: t9
      } = t0,
      t10 = _objectWithoutPropertiesLoose(t0, _excluded2);
    t1 = t4;
    anchor = t5;
    children = t6;
    t2 = t7;
    t3 = t8;
    xstyle = t9;
    restProps = t10;
    $[0] = t0;
    $[1] = anchor;
    $[2] = children;
    $[3] = restProps;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = xstyle;
  } else {
    anchor = $[1];
    children = $[2];
    restProps = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    xstyle = $[7];
  }
  const align = t1 === undefined ? "center" : t1;
  const keepMounted = t2 === undefined ? false : t2;
  const side = t3 === undefined ? "top" : t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      className: "x1btqs1c"
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== xstyle) {
    t5 = stylex.props(styles.popup, xstyle);
    $[9] = xstyle;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      [DATA_COMPONENT_ATTRIBUTE]: TOOLTIP_CONTENT_COMPONENT_NAME
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== children) {
    t7 = /*#__PURE__*/_jsx(LayoutConstraintsReset, {
      children: children
    });
    $[12] = children;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      className: "x1sd0qht x78zum5 xhm7rv7 xpahqiq x10103a0 x1v8u1c8 x1xhyldq xn6wvcl x1fngttz xu4l8yx x1jgqwxf"
    };
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = /*#__PURE__*/_jsx(BaseTooltip.Arrow, Object.assign({}, t8, {
      render: /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx("svg", {
          width: "100%",
          height: "100%",
          viewBox: "0 0 12 6",
          preserveAspectRatio: "none",
          xmlns: "http://www.w3.org/2000/svg",
          children: /*#__PURE__*/_jsx("path", Object.assign({
            d: "M6 0L12 6H0Z"
          }, {
            className: "xb9mk8z"
          }))
        })
      })
    }));
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== ref || $[17] !== restProps || $[18] !== t5 || $[19] !== t7) {
    t10 = /*#__PURE__*/_jsxs(BaseTooltip.Popup, Object.assign({}, restProps, {
      ref: ref
    }, t5, t6, {
      children: [t7, t9]
    }));
    $[16] = ref;
    $[17] = restProps;
    $[18] = t5;
    $[19] = t7;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== align || $[22] !== anchor || $[23] !== side || $[24] !== t10) {
    t11 = /*#__PURE__*/_jsx(BaseTooltip.Positioner, Object.assign({
      align: align,
      alignOffset: 0,
      anchor: anchor,
      arrowPadding: 10,
      side: side,
      sideOffset: 10
    }, t4, {
      children: t10
    }));
    $[21] = align;
    $[22] = anchor;
    $[23] = side;
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== keepMounted || $[27] !== t11) {
    t12 = /*#__PURE__*/_jsx(BaseTooltip.Portal, {
      keepMounted: keepMounted,
      children: t11
    });
    $[26] = keepMounted;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  return t12;
});
TooltipContent.displayName = TOOLTIP_CONTENT_COMPONENT_NAME;
export { TooltipProvider } from './TooltipProvider';